import React, { useState } from 'react';

// Composant placeholder pour les images de listings
export const ListingImagePlaceholder = ({ className = "w-full h-48" }) => (
  <div className={`${className} bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center`}>
    <div className="text-center text-gray-400">
      <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1.5} 
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
        />
      </svg>
      <p className="text-sm font-medium">Image non disponible</p>
    </div>
  </div>
);

// Composant placeholder pour les avatars
export const UserAvatarPlaceholder = ({ className = "w-6 h-6" }) => (
  <div className={`${className} rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center`}>
    <svg className="w-3/4 h-3/4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
      />
    </svg>
  </div>
);

// Composant placeholder pour les listings complets (skeleton loading)
export const ListingCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden animate-pulse">
    <div className="w-full h-48 bg-gray-300"></div>
    <div className="p-3">
      <div className="h-4 bg-gray-300 rounded mb-2"></div>
      <div className="h-6 bg-gray-300 rounded w-1/3 mb-2"></div>
      <div className="h-4 bg-gray-300 rounded w-2/3 mb-3"></div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
          <div className="h-4 bg-gray-300 rounded w-20"></div>
        </div>
        <div className="h-8 bg-gray-300 rounded w-16"></div>
      </div>
    </div>
  </div>
);

// Composant placeholder pour une grille de listings
export const ListingsGridSkeleton = ({ count = 4 }) => (
  <div className="p-4">
    <div className="h-6 bg-gray-300 rounded w-48 mb-4 animate-pulse"></div>
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <ListingCardSkeleton key={index} />
      ))}
    </div>
  </div>
);

// Composant d'erreur pour les images
export const ImageErrorFallback = ({ title = "Image non disponible", subtitle = "L'image n'a pas pu être chargée" }) => (
  <div className="w-full h-48 bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
    <div className="text-center text-gray-500">
      <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1} 
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" 
        />
      </svg>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-gray-400">{subtitle}</p>
    </div>
  </div>
);

// Composant placeholder pour les catégories
export const CategoryIconPlaceholder = ({ className = "text-3xl" }) => (
  <div className={`${className} bg-gray-200 rounded-lg w-12 h-12 flex items-center justify-center`}>
    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" 
      />
    </svg>
  </div>
);

// Composant d'image avancé avec système de placeholder intelligent
export const ImageWithPlaceholder = ({ 
  src, 
  alt, 
  className = "w-full h-48 object-cover",
  fallbackSrc = null,
  showLoadingAnimation = true,
  onLoad,
  onError,
  children
}) => {
  const [loadingState, setLoadingState] = useState('loading'); // loading, loaded, error
  const [currentSrc, setCurrentSrc] = useState(src);

  const handleLoad = () => {
    setLoadingState('loaded');
    if (onLoad) onLoad();
  };

  const handleError = () => {
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      return;
    }
    setLoadingState('error');
    if (onError) onError();
  };

  const generatePlaceholderUrl = (width = 400, height = 300) => {
    return `https://via.placeholder.com/${width}x${height}/f3f4f6/9ca3af?text=${encodeURIComponent(alt || 'Image non disponible')}`;
  };

  // Fonction pour générer des images SVG statiques (pas de dépendance réseau)
  const generateStaticSvgDataUrl = (width = 40, height = 40, text = 'No Image') => {
    const svg = `
      <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="${width}" height="${height}" fill="#F3F4F6"/>
        <text x="50%" y="50%" text-anchor="middle" dy="0.3em" fill="#9CA3AF" font-family="Arial, sans-serif" font-size="8">${text}</text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const LoadingPlaceholder = () => (
    <div className={`${className} bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200 animate-pulse relative overflow-hidden`}>
      {showLoadingAnimation && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer transform -skew-x-12"></div>
      )}
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-2 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-xs opacity-70">Chargement...</p>
        </div>
      </div>
    </div>
  );

  const ErrorPlaceholder = () => (
    <div className={`${className} bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border-2 border-dashed border-gray-300`}>
      <div className="text-center text-gray-500">
        <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-xs">Image non disponible</p>
      </div>
    </div>
  );

  if (!src && !fallbackSrc) {
    return <ErrorPlaceholder />;
  }

  return (
    <div className="relative">
      {loadingState === 'loading' && <LoadingPlaceholder />}
      
      {loadingState === 'error' ? (
        <ErrorPlaceholder />
      ) : (
        <img
          src={currentSrc || generatePlaceholderUrl()}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${loadingState === 'loading' ? 'opacity-0 absolute inset-0' : 'opacity-100'}`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
        />
      )}
      
      {children && (
        <div className="absolute inset-0 pointer-events-none">
          {children}
        </div>
      )}
    </div>
  );
};

// Avatar avec fallback automatique et initiales
export const AvatarWithFallback = ({ 
  src, 
  alt, 
  className = "w-6 h-6 rounded-full object-cover",
  fallbackText,
  showInitials = true 
}) => {
  const [hasError, setHasError] = useState(false);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!src || hasError) {
    if (showInitials && fallbackText) {
      return (
        <div className={`${className} bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-xs`}>
          {getInitials(fallbackText)}
        </div>
      );
    }
    return <UserAvatarPlaceholder className={className} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
};

// Placeholder pour écran vide
export const EmptyStatePlaceholder = ({ 
  title = "Aucun résultat trouvé", 
  description = "Essayez de modifier vos critères de recherche.",
  icon = "search",
  action
}) => {
  const icons = {
    search: (
      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    image: (
      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    inbox: (
      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4m16 0V9a2 2 0 00-2-2H6a2 2 0 00-2 2v4z" />
      </svg>
    )
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="mb-4">
        {icons[icon] || icons.search}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 max-w-sm mb-6">{description}</p>
      {action && (
        <div className="mt-4">
          {action}
        </div>
      )}
    </div>
  );
};

// Fonction utilitaire pour générer des images SVG statiques 
export const generateStaticImagePlaceholder = (width = 40, height = 40, text = 'No Image') => {
  const svg = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="${width}" height="${height}" fill="#F3F4F6"/>
      <text x="50%" y="50%" text-anchor="middle" dy="0.3em" fill="#9CA3AF" font-family="Arial, sans-serif" font-size="8">${text}</text>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};
